import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { AlertCircle, Layers, Plus } from "lucide-react"
import { useState } from "react"

import { api, ErrorApi } from "@/api/cliente"
import type { Cargo, Departamento } from "@/componentes/cargos/cargo"
import { documentoDeDepartamentos } from "@/componentes/cargos/exportarCargos"
import { DialogoDepartamento } from "@/componentes/departamentos/DialogoDepartamento"
import {
  PuestosDelArea,
  TarjetaDepartamento,
} from "@/componentes/departamentos/TarjetaDepartamento"
import { Exportar } from "@/componentes/exportar/Exportar"
import { KpiDepartamentos } from "@/componentes/kpi/KpiPlanificacion"
import { Boton } from "@/componentes/ui/Boton"
import { Modal } from "@/componentes/ui/Modal"
import { tienePermiso } from "@/lib/autorizacion"
import { usarSesion } from "@/lib/sesion"

/**
 * Las áreas de la agencia, cada una con los puestos que cuelgan de ella.
 *
 * ⚠ **Los puestos de cada área salen del catálogo de cargos, no de aquí.** Es el cargo
 * el que dice a qué área pertenece; el área no guarda la lista. Por eso un puesto se
 * cambia de área al editar el CARGO, y aquí se enlaza a él.
 *
 * ⚠ **Los cargos sin área salen aparte, al final.** Sin ese grupo, un puesto que nadie
 * ha colocado no aparecería en esta vista, y quien la mira creería que la agencia no
 * lo tiene.
 *
 * ⚠ **Se archiva, no se borra**, y antes de archivar se dice cuántos puestos tiene:
 * siguen perteneciendo a ella —es lo que explica dónde trabajaba su gente— pero deja
 * de ofrecerse al elegir el área de un cargo.
 */
export function CatalogoDepartamentos() {
  const clienteQuery = useQueryClient()
  const usuario = usarSesion((e) => e.usuario)
  const puedeAdministrar = tienePermiso(usuario, "departamento:administrar")
  const puedeVerCargos = tienePermiso(usuario, "cargo:ver")

  const [verArchivados, setVerArchivados] = useState(false)
  const [dialogo, setDialogo] = useState<{
    abierto: boolean
    departamento: Departamento | null
    vez: number
  }>({ abierto: false, departamento: null, vez: 0 })
  // El que se va a archivar NO se limpia al cerrar: el aviso se quedaría sin nombre
  // durante la animación de salida.
  const [aArchivar, setAArchivar] = useState<Departamento | null>(null)
  const [confirmando, setConfirmando] = useState(false)

  const departamentos = useQuery({
    queryKey: ["departamentos"],
    queryFn: () => api.get<Departamento[]>("/api/v1/departamentos"),
  })
  const cargos = useQuery({
    queryKey: ["cargos"],
    queryFn: () => api.get<Cargo[]>("/api/v1/cargos"),
  })

  const cambiarEstado = useMutation({
    mutationFn: ({ id, activo }: { id: number; activo: boolean }) =>
      api.patch<Departamento>(`/api/v1/departamentos/${id}`, { activo }),
    onSuccess: () => {
      clienteQuery.invalidateQueries({ queryKey: ["departamentos"] })
      setConfirmando(false)
    },
  })

  const abrirDialogo = (departamento: Departamento | null) =>
    setDialogo((d) => ({ abierto: true, departamento, vez: d.vez + 1 }))

  const pedirArchivar = (departamento: Departamento) => {
    cambiarEstado.reset()
    setAArchivar(departamento)
    setConfirmando(true)
  }

  if (departamentos.isPending) {
    return (
      <p role="status" className="py-12 text-center text-sm" style={{ color: "var(--fg-muted)" }}>
        Cargando…
      </p>
    )
  }

  if (departamentos.isError) {
    return (
      <p role="alert" className="py-12 text-center text-sm" style={{ color: "var(--destructive)" }}>
        {departamentos.error instanceof ErrorApi
          ? departamentos.error.message
          : "No se pudieron cargar los departamentos"}
      </p>
    )
  }

  const archivados = departamentos.data.filter((d) => !d.activo).length
  // Por código, como la estructura aprobada; las que no lo tienen, al final y en su orden.
  const visibles = departamentos.data
    .filter((d) => d.activo || verArchivados)
    .sort((a, b) => (a.codigo ?? "99").localeCompare(b.codigo ?? "99"))
  /** Los puestos de un área; `undefined` mientras el catálogo de cargos no llega. */
  const puestosDe = (area: number | null) => cargos.data?.filter((c) => c.departamento_id === area)
  const sinArea = puestosDe(null) ?? []

  return (
    <div>
      {/* Ni título ni frase propios: los pone la cabecera de «Planificación
          estratégica». Aquí arriba sólo van las acciones, a la derecha. */}
      <div className="mb-4 flex flex-wrap items-center justify-end gap-3">
        {archivados > 0 && (
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={verArchivados}
              onChange={(e) => setVerArchivados(e.target.checked)}
            />
            Ver los archivados ({archivados})
          </label>
        )}
        {visibles.length > 0 && (
          <Exportar
            titulo="Departamentos"
            documento={() => documentoDeDepartamentos(visibles, cargos.data, verArchivados)}
          />
        )}
        {puedeAdministrar && (
          <Boton variante="primario" onClick={() => abrirDialogo(null)}>
            <Plus size={16} aria-hidden="true" />
            Nuevo departamento
          </Boton>
        )}
      </div>
      <KpiDepartamentos />

      {cambiarEstado.error && !confirmando && (
        <p
          role="alert"
          className="mb-4 flex items-start gap-2 text-sm"
          style={{ color: "var(--destructive)" }}
        >
          <AlertCircle size={16} aria-hidden={true} />
          {cambiarEstado.error instanceof ErrorApi
            ? cambiarEstado.error.message
            : "No se pudo cambiar el departamento"}
        </p>
      )}

      {visibles.length === 0 && (
        <div
          className="mb-4 flex flex-col items-center gap-3 superficie py-12"
          style={{ color: "var(--fg-muted)" }}
        >
          <Layers size={24} aria-hidden={true} />
          <p className="px-4 text-center text-sm">
            {departamentos.data.length === 0
              ? "Todavía no hay departamentos. Cada cargo se coloca en el suyo al editarlo."
              : "Todos los departamentos están archivados."}
          </p>
        </div>
      )}

      {(visibles.length > 0 || sinArea.length > 0) && (
        <ul className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
          {visibles.map((d) => (
            <TarjetaDepartamento
              key={d.id}
              departamento={d}
              puestos={puestosDe(d.id)}
              puedeAdministrar={puedeAdministrar}
              puedeVerCargos={puedeVerCargos}
              ocupado={cambiarEstado.isPending}
              onEditar={() => abrirDialogo(d)}
              onArchivar={() => pedirArchivar(d)}
              onReactivar={() => cambiarEstado.mutate({ id: d.id, activo: true })}
            />
          ))}
          {sinArea.length > 0 && (
            <li
              data-testid="sin-departamento"
              className="tarjeta flex flex-col gap-3"
              style={{ borderStyle: "dashed" }}
            >
              <h2 className="text-base font-semibold">Sin departamento</h2>
              <p className="text-sm" style={{ color: "var(--fg-muted)" }}>
                Puestos que nadie ha colocado todavía: el área se elige al editar cada cargo.
              </p>
              <PuestosDelArea puestos={sinArea} cuantos={sinArea.length} enlazar={puedeVerCargos} />
            </li>
          )}
        </ul>
      )}

      <DialogoDepartamento
        key={dialogo.vez}
        abierto={dialogo.abierto}
        departamento={dialogo.departamento}
        cargos={cargos.data ?? []}
        areas={departamentos.data}
        onCerrar={() => setDialogo((d) => ({ ...d, abierto: false }))}
      />

      <Modal
        abierto={confirmando}
        titulo={`Archivar «${aArchivar?.nombre ?? ""}»`}
        descripcion="Se archiva, no se borra: se puede volver a activar cuando haga falta."
        ancho="sm"
        onCerrar={() => setConfirmando(false)}
      >
        <p className="text-sm">
          {aArchivar !== null && aArchivar.cargos > 0
            ? `Tiene ${aArchivar.cargos} cargo${aArchivar.cargos === 1 ? "" : "s"}. Seguirán en él —es lo que explica dónde trabajaba su gente—, pero dejará de ofrecerse al elegir el área de un cargo.`
            : "No tiene cargos. Dejará de ofrecerse al elegir el área de un cargo."}
        </p>
        {cambiarEstado.error && (
          <p role="alert" className="mt-3 text-sm" style={{ color: "var(--destructive)" }}>
            {cambiarEstado.error instanceof ErrorApi
              ? cambiarEstado.error.message
              : "No se pudo archivar"}
          </p>
        )}
        <div className="mt-4 flex justify-end gap-2">
          <button
            type="button"
            onClick={() => setConfirmando(false)}
            className="boton-secundario h-9 px-3 text-sm"
          >
            Cancelar
          </button>
          <button
            type="button"
            disabled={cambiarEstado.isPending || aArchivar === null}
            onClick={() => {
              if (aArchivar !== null) cambiarEstado.mutate({ id: aArchivar.id, activo: false })
            }}
            className="boton-primario h-9 px-3 text-sm"
          >
            Archivar
          </button>
        </div>
      </Modal>
    </div>
  )
}
